import { useState, useEffect } from 'react';
import { AlertCircle, ChevronDown } from 'lucide-react';
import { GAP_SEVERITY_STYLES } from '../constants';
import { gapRowId } from '../builders';
import type { GapRowVM, GapsSummaryVM } from '../types';

const GRID = '1.1fr 2fr 1fr 0.9fr 1.8fr';

function HeadCell({ label, center }: { label: string; center?: boolean }) {
  return (
    <div
      style={{
        padding: '11px 14px',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        color: 'var(--muted-foreground)',
        textAlign: center ? 'center' : 'left',
      }}
    >
      {label}
    </div>
  );
}

// Gaps & opportunities (DC §8): collapsible card with the gap-overview table.
// Accepts a deep-link from optimization cards: `expandSignal` forces it open and
// `highlightAttribute` briefly highlights the matching gap row.
export function GapsSection({
  rows,
  summary,
  expandSignal,
  highlightAttribute,
}: {
  rows: GapRowVM[];
  summary: GapsSummaryVM;
  expandSignal?: number;
  highlightAttribute?: string | null;
}) {
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (expandSignal) setOpen(true);
  }, [expandSignal]);

  const highlightId = highlightAttribute ? gapRowId(highlightAttribute) : null;

  return (
    <div
      id="cognition-gaps"
      style={{
        marginTop: 26,
        border: '1px solid var(--border)',
        borderRadius: 12,
        background: 'var(--card)',
        overflow: 'hidden',
      }}
    >
      <div
        onClick={() => setOpen((v) => !v)}
        style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '20px 22px', cursor: 'pointer' }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 38,
            height: 38,
            borderRadius: 10,
            background: 'var(--infrared-coral-10)',
            color: 'var(--infrared-coral)',
            flexShrink: 0,
          }}
        >
          <AlertCircle style={{ width: 18, height: 18 }} />
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--foreground)', margin: '0 0 3px' }}>
            Gaps &amp; opportunities: where AI's understanding can be influenced
          </h3>
          <p style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--muted-foreground)', margin: 0 }}>
            Incorrect beliefs, missing attributes, and inconsistent interpretations that distort how AI ranks your product.
          </p>
        </div>

        {!open && summary.total > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexShrink: 0, marginRight: 4 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 24,
                padding: '0 10px',
                borderRadius: 6,
                background: 'var(--infrared-coral-10)',
                color: 'var(--infrared-coral)',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {summary.total} gaps
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--infrared-coral)' }}>{summary.high}H</span>
            <span style={{ color: 'var(--silver-mist)', fontSize: 11 }}>·</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--warning-text)' }}>{summary.moderate}M</span>
            <span style={{ color: 'var(--silver-mist)', fontSize: 11 }}>·</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted-foreground)' }}>{summary.low}L</span>
          </div>
        )}

        <span
          style={{
            display: 'inline-flex',
            color: 'var(--muted-foreground)',
            flexShrink: 0,
            marginTop: 3,
            transition: 'transform var(--duration-base) var(--ease-standard)',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <ChevronDown style={{ width: 18, height: 18 }} />
        </span>
      </div>

      {open && (
        <div style={{ padding: '2px 22px 22px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--foreground)', marginBottom: 12 }}>Gap overview</div>

          {rows.length === 0 ? (
            <div style={{ padding: 16, borderRadius: 10, background: 'var(--muted)', border: '1px solid var(--border)' }}>
              <p style={{ fontSize: 13, color: 'var(--muted-foreground)', margin: 0 }}>No gaps returned for this run.</p>
            </div>
          ) : (
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: GRID, background: 'var(--alpha-teal-5)', borderBottom: '1px solid var(--border)' }}>
                <HeadCell label="Attribute" />
                <HeadCell label="Gap reason" />
                <HeadCell label="Models affected" center />
                <HeadCell label="Severity" center />
                <HeadCell label="Impact" />
              </div>
              {rows.map((g, idx) => {
                const sev = GAP_SEVERITY_STYLES[g.severity] || GAP_SEVERITY_STYLES.Low;
                const rowId = gapRowId(g.attribute);
                const highlighted = highlightId === rowId;
                return (
                  <div
                    key={`${g.attribute}-${idx}`}
                    id={rowId}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: GRID,
                      borderBottom: '1px solid var(--border)',
                      alignItems: 'center',
                      background: highlighted ? 'var(--alpha-teal-5)' : 'transparent',
                      boxShadow: highlighted ? 'inset 3px 0 0 var(--alpha-teal)' : 'none',
                      transition: 'background var(--duration-base) var(--ease-standard)',
                    }}
                  >
                    <div style={{ padding: '13px 14px', fontSize: 13, fontWeight: 600, color: 'var(--foreground)', lineHeight: 1.35 }}>
                      {g.attribute}
                    </div>
                    <div style={{ padding: '13px 14px', fontSize: 13, lineHeight: 1.5, color: 'var(--muted-foreground)' }}>
                      {g.reason}
                    </div>
                    <div style={{ padding: '13px 14px', fontSize: 12, color: 'var(--muted-foreground)', textAlign: 'center' }}>
                      {g.models}
                    </div>
                    <div style={{ padding: '13px 14px', display: 'flex', justifyContent: 'center' }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          height: 22,
                          padding: '0 10px',
                          borderRadius: 6,
                          background: sev.bg,
                          color: sev.color,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {g.severity}
                      </span>
                    </div>
                    <div style={{ padding: '13px 14px', fontSize: 12, lineHeight: 1.5, color: 'var(--muted-foreground)' }}>
                      {g.impact}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
